import { type OrgSlugParams, orgSlugParamsSchema } from "@/lib/portal-schemas";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
const WEB_API_BASE =
	(import.meta.env.VITE_WEB_API_BASE as string) || "http://localhost:8080";
const ATTORNEY_API_BASE =
	(import.meta.env.VITE_ATTORNEY_API_URL as string) || "http://localhost:8081";
const USE_SAMPLE =
	((import.meta.env.VITE_USE_SAMPLE as string) || "true") === "true";

const SAMPLE_PORTAL = {
	title: "Preview: Sample Client Portal",
	blocks: [
		{ type: "hero", heading: "Welcome", subheading: "This is a preview" },
	],
};

export default function PortalPreviewRoute() {
	const params = useParams({ strict: false }) as unknown as OrgSlugParams;
	const parsed = orgSlugParamsSchema.safeParse(params);
	if (!parsed.success) {
		return (
			<main style={{ padding: 24 }}>
				<h1>Invalid parameters</h1>
				<p>Expected org/slug with alphanumeric and dashes.</p>
				<p>
					<Link to="/">Go home</Link>
				</p>
			</main>
		);
	}
	const { org, slug } = parsed.data;

	const isDraft =
		typeof window !== "undefined" &&
		new URLSearchParams(window.location.search).get("draft") === "true";
	const { data, isLoading, error } = useQuery({
		queryKey: ["portal-config", org, slug],
		queryFn: async () => {
			if (USE_SAMPLE) return SAMPLE_PORTAL;
			const url = isDraft
				? `${ATTORNEY_API_BASE.replace(/\/$/, "")}/api/attorneys/portals/${encodeURIComponent(org)}/${encodeURIComponent(slug)}/draft`
				: `${WEB_API_BASE.replace(/\/$/, "")}/api/portal-config/${encodeURIComponent(org)}/${encodeURIComponent(slug)}`;
			const res = await fetch(url, { headers: { Accept: "application/json" } });
			if (!res.ok) throw new Error(`Failed to load portal: ${res.status}`);
			return (await res.json()) as Record<string, unknown>;
		},
	});

	if (isLoading)
		return (
			<main style={{ padding: 24 }}>
				<p>Loading preview…</p>
			</main>
		);
	if (error)
		return (
			<main style={{ padding: 24 }}>
				<p>Error: {(error as Error).message}</p>
			</main>
		);

	type BlockDef = { type?: string; [k: string]: unknown };
	const portal = (data ?? SAMPLE_PORTAL) as {
		title?: string;
		blocks?: BlockDef[];
	};
	return (
		<main style={{ padding: 24, display: "grid", gap: 16 }}>
			<header>
				<h1>Preview — {portal.title ?? `${org}/${slug}`}</h1>
				<p style={{ display: "flex", gap: 8 }}>
					<Link to="/portals/$org/$slug/edit" params={{ org, slug }}>
						Edit
					</Link>
					<a href={`?draft=${isDraft ? "false" : "true"}`}>
						{isDraft ? "View Published" : "View Draft"}
					</a>
				</p>
			</header>
			{Array.isArray(portal.blocks) &&
				portal.blocks.map((b, i: number) => (
					<Block key={JSON.stringify(b)} block={b} />
				))}
		</main>
	);
}

function Block({ block }: { block: { type?: string; [k: string]: unknown } }) {
	if (!block || typeof block !== "object") return null;
	switch (block.type) {
		case "hero":
			return (
				<section
					style={{
						padding: 16,
						borderRadius: 8,
						background: "var(--surface-2, #f3f4f6)",
					}}
				>
					<h2 style={{ margin: 0 }}>{String(block.heading ?? "Hero")}</h2>
					{block.subheading ? (
						<p style={{ marginTop: 8 }}>{String(block.subheading)}</p>
					) : null}
				</section>
			);
		default:
			return (
				<section>
					<pre style={{ fontSize: 12, opacity: 0.8 }}>
						{JSON.stringify(block, null, 2)}
					</pre>
				</section>
			);
	}
}
